import { useQueries } from '@tanstack/react-query';
import { addMonths, startOfMonth } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getBookingScheduleQuery } from '@/api/booking/getBookingScheduleQuery';
import type { BarberScheduleResponse } from '@/api/booking/getBookingScheduleQuery/types';
import {
  buildAppointmentsForSchedule,
  getGroupScheduleQuery,
} from '@/api/booking/getGroupScheduleQuery';
import type { GroupScheduleResponse } from '@/api/booking/getGroupScheduleQuery/types';
import type { Cart, Professional, Service } from '@/types/booking';
import { transformServiceIds } from '@/utils/bookingCartUtils';
import { DateFormat, formatDate, getToday } from '@/utils/dateUtils';
import { getMonthFetchRange } from '@/utils/scheduleRangeUtils';
import {
  AVAILABILITY_WAITLIST_ONLY,
  buildAvailabilityMap,
  filterScheduleForMultipleServices,
} from '@/utils/timeSlotUtils';

import { transformGroupScheduleToGuestSchedules } from './transformGroupSchedule';

const MONTH_KEY_FORMAT = DateFormat.API_DATE;

interface UseBookingScheduleQueriesOptions {
  shopId: string;
  cart: Cart | null | undefined;
  currentMonth: Date;
  /**
   * Month-aligned start of the availability fetch window (from
   * `useReservationInterval.rangeStart`).
   */
  rangeStart: Date;
  /**
   * Month-aligned end of the availability fetch window (from
   * `useReservationInterval.rangeEnd`). Drives how far ahead availability is
   * fetched so a long barber window (e.g. 120 days) is bookable.
   */
  rangeEnd: Date;
  timezone?: string;
  /**
   * Services list used to transform parent IDs to service IDs for the schedule query.
   * Required for correct availability calculation when cart items have parent IDs.
   */
  services?: Service[];
  /**
   * Professionals list used to filter barberIds in time slots for "any barber" bookings
   * with multiple services. Barbers that explicitly disallow multiple services are excluded.
   */
  professionals?: Professional[];
  /** When true, days where the barber works but has no slots are marked as waitlist-only (selectable). */
  waitlistEnabled?: boolean;
}

export const useBookingScheduleQueries = ({
  shopId,
  cart,
  currentMonth,
  rangeStart,
  rangeEnd,
  timezone,
  services,
  professionals,
  waitlistEnabled = false,
}: UseBookingScheduleQueriesOptions) => {
  const appointments = useMemo(() => cart?.items || [], [cart?.items]);
  const isGroupAppointment = appointments.length > 1;

  const currentMonthStart = startOfMonth(currentMonth);
  const currentMonthKey = formatDate(currentMonthStart, MONTH_KEY_FORMAT);

  // Identity of the booking window; changes when the barber/window changes.
  const windowKey = `${formatDate(rangeStart, MONTH_KEY_FORMAT)}..${formatDate(
    rangeEnd,
    MONTH_KEY_FORMAT,
  )}`;

  // Lazily fetch availability one month at a time instead of the whole window.
  // We remember every month the user has visited so paging back is instant
  // (cached), and we additionally prefetch the next month so paging forward
  // feels immediate. This keeps load light even when a barber's advance-book
  // window is very large (e.g. 120 days).
  const [visitedMonthKeys, setVisitedMonthKeys] = useState<string[]>([
    currentMonthKey,
  ]);
  // Track the window the visited set belongs to, so a window change resets it.
  const visitedWindowKeyRef = useRef(windowKey);

  useEffect(() => {
    setVisitedMonthKeys((prev) => {
      // When the window changes (barber/window switch), drop months visited
      // under the old (possibly longer) window so they stop issuing queries.
      if (visitedWindowKeyRef.current !== windowKey) {
        visitedWindowKeyRef.current = windowKey;
        return [currentMonthKey];
      }
      return prev.includes(currentMonthKey) ? prev : [...prev, currentMonthKey];
    });
  }, [currentMonthKey, windowKey]);

  // The months we actually issue queries for: every visited month plus the
  // next-month prefetch, each clamped to the booking window (skipping any that
  // fall entirely outside it). Deduped and tagged with whether it's the month
  // currently shown (which drives the loading state vs. silent prefetch).
  const monthFetchPlan = useMemo(() => {
    // Include currentMonthKey explicitly even though the effect above also adds
    // it: the effect runs AFTER render, so on the first render following a month
    // change visitedMonthKeys doesn't contain it yet. Without this guard the
    // current month's query would be delayed by one render.
    const keys = new Set(
      visitedMonthKeys.includes(currentMonthKey)
        ? visitedMonthKeys
        : [...visitedMonthKeys, currentMonthKey],
    );

    // Prefetch the month after the one being viewed.
    const nextMonthStart = startOfMonth(addMonths(currentMonthStart, 1));
    keys.add(formatDate(nextMonthStart, MONTH_KEY_FORMAT));

    return Array.from(keys)
      .map((monthKey) => {
        const monthStart = startOfMonth(new Date(`${monthKey}T00:00:00`));
        const range = getMonthFetchRange(monthStart, rangeStart, rangeEnd);
        if (!range) return null;
        return {
          monthKey,
          isCurrentMonth: monthKey === currentMonthKey,
          startDate: range.startDate,
          endDate: range.endDate,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [
    visitedMonthKeys,
    currentMonthKey,
    currentMonthStart,
    rangeStart,
    rangeEnd,
  ]);

  // Build appointments array for the v2 group schedule endpoint
  // Pass services to transform parent IDs to service IDs for correct availability
  const appointmentsForSchedule = useMemo(() => {
    return buildAppointmentsForSchedule(appointments, services);
  }, [appointments, services]);

  // For single appointments, use the v1 endpoint (existing behavior)
  const guestScheduleParams = useMemo(() => {
    if (isGroupAppointment) return [];

    return (cart?.items || []).map((cartItem) => {
      const isAnyProfessional =
        cartItem.bookedWithAnyBarber || !cartItem.barber;

      // Transform parent IDs to service IDs for correct availability calculation
      const serviceIds = transformServiceIds(
        cartItem.services.map((s) => s.id),
        services,
      );

      return {
        guestId: cartItem.id,
        barberId: isAnyProfessional ? null : (cartItem.barber?.id ?? null),
        serviceDuration:
          cartItem.subItems?.reduce(
            (total, service) => total + service.duration,
            0,
          ) ?? 0,
        serviceId: isAnyProfessional ? serviceIds.join(';') || null : null,
      };
    });
  }, [cart?.items, isGroupAppointment, services]);

  // V1 queries for single appointments — one query per (guest × month). The
  // months come from the lazy fetch plan (visited months + next-month
  // prefetch), so only the visible month and its prefetch load at a time;
  // cached months are merged as the user pages, keeping a long booking window
  // cheap.
  const singleScheduleQueries = useQueries({
    queries: guestScheduleParams.flatMap((params) =>
      monthFetchPlan.map((month) =>
        getBookingScheduleQuery({
          shopId: shopId || '',
          startDate: month.startDate,
          endDate: month.endDate,
          barberId: params.barberId,
          serviceDuration: params.serviceDuration,
          serviceId: params.serviceId,
        }),
      ),
    ),
    // Group the flat (guest × month) results back per guest: merge each guest's
    // months into one schedule array, and derive the per-guest loading/error
    // state from the CURRENT month's query (prefetch months stay silent).
    combine: (queries) => {
      const monthCount = monthFetchPlan.length;
      return guestScheduleParams.map((params, guestIndex) => {
        const guestQueries = queries.slice(
          guestIndex * monthCount,
          guestIndex * monthCount + monthCount,
        );

        const mergedData = guestQueries.reduce<BarberScheduleResponse[]>(
          (acc, q) =>
            q.data ? acc.concat(q.data as BarberScheduleResponse[]) : acc,
          [],
        );

        const currentMonthIndex = monthFetchPlan.findIndex(
          (m) => m.isCurrentMonth,
        );
        const currentQuery =
          currentMonthIndex >= 0
            ? guestQueries[currentMonthIndex]
            : guestQueries[0];

        return {
          guestId: appointments[guestIndex]?.id ?? params.guestId,
          data: mergedData.length > 0 ? mergedData : undefined,
          // Loading/error reflect the visible month; prefetch is silent.
          isLoading: currentQuery?.isLoading ?? false,
          isFetching: currentQuery?.isFetching ?? false,
          isError: currentQuery?.isError ?? false,
          error: currentQuery?.error || null,
          refetch: () => guestQueries.forEach((q) => q.refetch()),
          isRefetching: currentQuery?.isRefetching ?? false,
        };
      });
    },
  });

  // V2 queries for group appointments — one query per month, using the same
  // lazy fetch plan as v1. Fetching per month (instead of one rolling
  // current+next span) is what keeps a selected day's availability alive when
  // the user pages the expanded calendar to another month: paging adds a month
  // to the plan rather than replacing the span, so the selected day's month
  // stays mounted and its slots keep rendering (SQR-14310).
  //
  // The v2 index-based transform is order-sensitive within a day
  // (`barberTimes[i]` ↔ appointment `i`), but days are independent — so each
  // month's response is transformed on its own and the per-guest results are
  // concatenated, preserving that mapping.
  const groupScheduleQueries = useQueries({
    queries: monthFetchPlan.map((month) => ({
      ...getGroupScheduleQuery({
        shopId,
        startDate: month.startDate,
        endDate: month.endDate,
        appointments: appointmentsForSchedule,
      }),
      enabled: isGroupAppointment && appointmentsForSchedule.length > 0,
    })),
    // Merge every planned month into one response, and derive the
    // loading/error state from the CURRENT month's query so prefetch months
    // stay silent (same contract as the v1 combine above).
    combine: (queries) => {
      const currentMonthIndex = monthFetchPlan.findIndex(
        (m) => m.isCurrentMonth,
      );
      const currentQuery =
        currentMonthIndex >= 0 ? queries[currentMonthIndex] : queries[0];

      return {
        data: queries.reduce<GroupScheduleResponse>(
          (acc, q) =>
            q.data ? acc.concat(q.data as GroupScheduleResponse) : acc,
          [],
        ),
        isLoading: currentQuery?.isLoading ?? false,
        isFetching: currentQuery?.isFetching ?? false,
        isError: currentQuery?.isError ?? false,
        error: currentQuery?.error || null,
        isRefetching: currentQuery?.isRefetching ?? false,
        // Whether the CURRENT month has usable data — the aggregate above can
        // be non-empty on prefetch data alone while the visible month is still
        // in flight.
        hasCurrentMonthData:
          ((currentQuery?.data as GroupScheduleResponse | undefined)?.length ??
            0) > 0,
        refetchAll: () => Promise.all(queries.map((q) => q.refetch())),
      };
    },
  });

  // `placeholderData: keepPreviousData` can't carry the group response across a
  // key change: it hands a query its OWN previous data, but `useQueries`
  // rebuilds the query array every render, so `data` comes back empty
  // mid-flight and the screen would flash the skeleton on every timeslot tap.
  // Retaining the last response reproduces what water-on-web gets from a
  // single `useQuery`: slots stay mounted, merely disabled, until the new one
  // lands. Keyed on the current month (not the aggregate, which a prefetch
  // month can fill on its own) and released once the fetch settles, so a
  // genuinely empty response still surfaces rather than leaving unbookable
  // slots on screen.
  const lastGroupScheduleRef = useRef<GroupScheduleResponse>([]);
  if (groupScheduleQueries.hasCurrentMonthData) {
    lastGroupScheduleRef.current = groupScheduleQueries.data;
  }
  const groupScheduleData =
    groupScheduleQueries.hasCurrentMonthData || !groupScheduleQueries.isFetching
      ? groupScheduleQueries.data
      : lastGroupScheduleRef.current;

  // Transform group schedule response to per-guest schedules
  // Uses index-based matching to handle ID changes during cart mutations
  const groupScheduleByIndex = useMemo(() => {
    if (!isGroupAppointment || groupScheduleData.length === 0) {
      return null;
    }

    return transformGroupScheduleToGuestSchedules(
      groupScheduleData,
      appointments.length,
    );
  }, [isGroupAppointment, groupScheduleData, appointments.length]);

  // Combine both query types into a unified schedule queries array
  const scheduleQueries = useMemo(() => {
    if (isGroupAppointment) {
      // For group appointments, create schedule query objects from the group query
      // Use index-based lookup to match appointments with their schedules
      // This handles ID changes during cart mutations with keepPreviousData
      return appointments.map((appointment, index) => ({
        guestId: appointment.id,
        data: groupScheduleByIndex?.get(index),
        isLoading: groupScheduleQueries.isLoading,
        isFetching: groupScheduleQueries.isFetching,
        isError: groupScheduleQueries.isError,
        error: groupScheduleQueries.error || null,
        refetch: groupScheduleQueries.refetchAll,
        isRefetching: groupScheduleQueries.isRefetching,
      }));
    }

    return singleScheduleQueries;
  }, [
    isGroupAppointment,
    appointments,
    groupScheduleByIndex,
    groupScheduleQueries,
    singleScheduleQueries,
  ]);

  // Build a Set of barber IDs that explicitly disallow multiple services.
  // Barbers with allowMultipleServices === false are excluded from "any barber" time slots
  // when the cart item has multiple services selected.
  const barberIdsDisallowingMultipleServices = useMemo(() => {
    if (!professionals) return null;

    const disallowed = professionals
      .filter((p) => p.barber.allowMultipleServices === false)
      .map((p) => p.barber.id);

    return disallowed.length > 0 ? new Set(disallowed) : null;
  }, [professionals]);

  // Filter barberIds in schedule data for "any barber" bookings with multiple services.
  // This ensures barbers that don't allow multiple services aren't assigned when
  // the customer selected "Any Professional" and picked more than one service.
  const filteredScheduleQueries = useMemo(() => {
    if (!barberIdsDisallowingMultipleServices) return scheduleQueries;

    return scheduleQueries.map((query, index) => {
      const cartItem = appointments[index];
      if (!cartItem) return query;

      const isAnyBarber = cartItem.bookedWithAnyBarber || !cartItem.barber;
      const hasMultipleServices = cartItem.services.length > 1;

      // Only filter if "any barber" AND multiple services are selected
      if (!isAnyBarber || !hasMultipleServices) return query;

      return {
        ...query,
        data: filterScheduleForMultipleServices(
          query.data,
          barberIdsDisallowingMultipleServices,
        ),
      };
    });
  }, [scheduleQueries, appointments, barberIdsDisallowingMultipleServices]);

  const availabilityMap = useMemo(() => {
    const queriesWithBarbers = filteredScheduleQueries.map((q, index) => {
      const cartItem = appointments[index];
      const professional = professionals?.find(
        (p) => p.barber.id === cartItem?.barber?.id,
      );
      return { ...q, barber: professional?.barber };
    });
    const map = buildAvailabilityMap(queriesWithBarbers, { waitlistEnabled });

    // Today must always be selectable — the API may prune past slots or the
    // barber may be off, but the user can still navigate to today (empty state
    // or waitlist button will appear as appropriate).
    const todayKey = formatDate(getToday(timezone), DateFormat.API_DATE);
    if (!map.has(todayKey) || map.get(todayKey) === 0) {
      map.set(todayKey, AVAILABILITY_WAITLIST_ONLY);
    }

    return map;
  }, [
    filteredScheduleQueries,
    waitlistEnabled,
    appointments,
    professionals,
    timezone,
  ]);

  // Refetch schedules - for group appointments this triggers a single refetch,
  // for single appointments it refetches all individual queries
  const refetchSchedules = useCallback(() => {
    if (isGroupAppointment) {
      return groupScheduleQueries.refetchAll();
    }
    return Promise.all(scheduleQueries.map((query) => query.refetch()));
  }, [isGroupAppointment, groupScheduleQueries, scheduleQueries]);

  // Check if any schedule query is currently fetching (for disabling buttons during fetch)
  // We use isFetching instead of isRefetching because when the query key changes
  // (with keepPreviousData), isRefetching is false but isFetching is true
  const isRefetchingSchedules = isGroupAppointment
    ? groupScheduleQueries.isFetching
    : singleScheduleQueries.some((q) => q.isRefetching);

  return {
    scheduleQueries: filteredScheduleQueries,
    availabilityMap,
    appointments,
    refetchSchedules,
    isGroupAppointment,
    isRefetchingSchedules,
  };
};
